import { ShieldCheck, Leaf, Users, Sparkles, Award, HeartHandshake } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { IMAGES, BRAND, waLink } from "@/data/site";

const REASONS = [
  {
    icon: Award,
    title: "Certified therapists",
    text: "Every therapist at Ody is professionally trained and certified — in Swedish, Thai, Balinese and deep tissue traditions — before they ever touch a guest.",
  },
  {
    icon: ShieldCheck,
    title: "Hyper-hygienic suites",
    text: "Suites are sanitised between every session. Linens are single-use and freshly laundered, tools sterilised, and products sealed until the moment they are opened.",
  },
  {
    icon: Leaf,
    title: "Premium organic oils",
    text: "We use cold-pressed, premium-grade oils and botanicals — chosen for skin benefit first, aroma second, and never the other way around.",
  },
  {
    icon: Users,
    title: "True privacy",
    text: "No shared rooms, no thin curtains. Every service happens behind a closed door in your own private suite — including dedicated couple suites.",
  },
  {
    icon: Sparkles,
    title: "Signature services",
    text: "From the four hand choreography to the 24K gold facial, our premium collection is crafted in-house and unavailable anywhere else in the neighbourhood.",
  },
  {
    icon: HeartHandshake,
    title: "Unhurried, always",
    text: "Sessions are scheduled with breathing room. Your therapist will never watch the clock, and you will never be rushed out of a suite.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us — Best Luxury Spa in Manikonda, Hyderabad | Ody Luxurious Spa"
        description="Why guests choose Ody Luxurious Spa Manikonda: certified massage therapists, hyper-hygienic private spa suites, premium organic oils and unhurried luxury service."
        keywords="about Ody Luxurious Spa, best spa Manikonda, luxury spa Hyderabad, certified massage therapists, hygienic spa Hyderabad"
      />
      <PageHero
        eyebrow="About Ody"
        title="A sanctuary hidden in plain sight"
        subtitle="Above the bustle of Pipe Line Road in Manikonda, behind an unassuming door, the city falls silent. This is the story of why we built Ody."
        crumbs={[{ label: "About" }]}
        image={IMAGES.aboutInterior}
      />
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal>
              <Eyebrow>Our Story</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-serif font-normal tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl leading-snug">
                Built on a simple observation:{" "}
                <span className="italic text-bronze">Hyderabad never stops.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-stone2">
                Ody Luxurious Spa began with a question its founders kept asking: where does this
                city go to be quiet? The answers were either far away, or fell short of what true
                rest demands — privacy, hygiene, and therapists who treat massage as craft rather
                than routine.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-5 text-base leading-relaxed text-stone2">
                So we built the answer ourselves in Manikonda: private suites instead of curtains,
                certified hands instead of rushed routines, and a menu of seventeen services that
                ranges from the timeless Swedish massage to experiences as rare as molten candle
                therapy and 24K gold. Every detail — the light, the scent, the silence — is
                deliberate.
              </p>
            </Reveal>
          </div>
          <div className="relative">
            <Reveal>
              <div className="rounded-3xl overflow-hidden">
                <img
                  src={IMAGES.aboutInterior}
                  alt="Inside the serene interiors of Ody Luxurious Spa"
                  className="w-full h-[420px] sm:h-[520px] object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={0.25} className="absolute -bottom-8 -left-4 sm:-left-8 w-44 sm:w-56">
              <div className="float-slow rounded-2xl overflow-hidden border-4 border-sand shadow-[0_20px_50px_rgba(31,29,26,0.18)]">
                <img
                  src={IMAGES.aboutTherapist}
                  alt="Therapist performing a traditional massage service"
                  className="w-full h-40 sm:h-48 object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="py-20 sm:py-28 bg-alabaster">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow className="text-center">Why Choose Us</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 text-center font-serif font-normal tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl">
              Six reasons guests become regulars
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} delay={(i % 3) * 0.1}>
                <div
                  data-testid={`reason-${r.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="h-full rounded-2xl border border-bronze/10 bg-cream p-8 transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-bronze/30"
                >
                  <span className="inline-flex rounded-full bg-bronze/10 p-3.5 text-bronze">
                    <r.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-ink">{r.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone2">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 text-center">
              <p className="text-sm text-stone2 mb-6">Come judge us in person — the first hello is on us.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href={BRAND.tel}
                  data-testid="about-call-btn"
                  className="inline-flex items-center gap-3 rounded-full bg-ink text-cream px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-bronze"
                >
                  Call {BRAND.phoneDisplay}
                </a>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-whatsapp-btn"
                  className="inline-flex items-center gap-3 rounded-full bg-wa px-8 py-4 text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-wa-dark"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
